import React, { useEffect, useRef, useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

interface EditableTitleProps {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onSave: (newValue: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  ariaLabel?: string;
  showEditAffordance?: boolean;
  editTriggerOnly?: boolean;
}

const MAX_LEN = 255;

const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  placeholder,
  disabled,
  onSave,
  className,
  inputClassName,
  ariaLabel = 'Edit title',
  showEditAffordance = true,
  editTriggerOnly = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEdit = () => {
    if (disabled) return;
    setDraft(value);
    setIsEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === value) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={saving}
        maxLength={MAX_LEN}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        aria-label={ariaLabel}
        className={
          inputClassName ??
          'bg-transparent border-b border-(--color-accent) outline-none px-1 text-(--color-text-primary) w-full'
        }
      />
    );
  }

  if (editTriggerOnly) {
    return (
      <span className={`group inline-flex items-center gap-2 ${className ?? ''}`}>
        <span className={value ? '' : 'text-(--color-text-muted) italic'}>{value || placeholder || ''}</span>
        {showEditAffordance && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              startEdit();
            }}
            aria-label={ariaLabel}
            title="Rename"
            className="cursor-pointer"
          >
            <PencilSquareIcon className="h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      disabled={disabled}
      className={`group inline-flex items-center gap-2 text-left ${
        disabled ? 'cursor-default' : 'cursor-pointer hover:opacity-80'
      } ${className ?? ''}`}
      aria-label={ariaLabel}
      title={disabled ? undefined : 'Click to rename'}
    >
      <span className={value ? '' : 'text-(--color-text-muted) italic'}>{value || placeholder || ''}</span>
      {showEditAffordance && !disabled && (
        <PencilSquareIcon className="h-4 w-4 opacity-60 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
};

export default EditableTitle;
